import {
	ReactElement,
	Ref,
	RefObject,
	useCallback,
	useMemo,
	useRef,
	useState
} from "react";
import {localize} from "client_legacy/ndaw/culture";
import {
	AppModel,
	AppStoreModel,
	AppStorePermissionsClientModel,
	disableApp,
	getTileInstanceCount
} from "client/Api/Apps/AppStore/appStore";
import { AppType } from "client/Api/Apps/common";
import {
	Button,
	Checkbox,
	ConfirmationDialog,
	ContentWithSidebar,
	Dialog,
	FormattedText,
	NoResults,
	SearchInputForm,
	SidebarHandle,
	Toolbar,
	TreeAccordion
} from "client/Components";
import {AppCard, PrimaryAction} from "./AppCard";
import {AppCardList} from "./AppCardList";
import {HttpRequestError} from "client/Api/httpClient";
import {AppStoreEvent, publish} from "client_legacy/Apps/AppStore/events";
import {AppStoreViewKind} from "./AppStoreDialog";
import {
	categories,
	CategoryEntry,
	CategoryFilter,
	getCategoryName,
	hiddenCategoryIds
} from "./CategoryFilter";
import {
	AlertManager,
	createLocalDataSource,
	useScrollToTop,
	useUserScopedKey,
	useAlerts,
	useWorkspaceMediaQuery,
	MediaQueryKind
} from "client/Hooks";
import { Guid } from "client_legacy/ndaw/guid";
import {contains} from "client/Data/searching";
import {generateTrackingId} from "client/Misc/utils";
import { useToggleExpansion } from "client/Components/TreeAccordion/useToggleExpansion";
import { AppTarget } from "./EditApp/EditAppDialogContent";
import { AppStoreLayout } from "./AppStoreLayout";

const useAppsDataSource = createLocalDataSource<AppModel, AppStoreFilterConfiguration>()
	.setConfiguration({
		filtering: {
			applyFilter: (items, filterConfiguration) => items.filter(app => {
				if (filterConfiguration.category.id !== null) {
					if (app.categoryId !== filterConfiguration.category.id) {
						return false;
					}
				}

				if (filterConfiguration.access.length > 0) {
					if (!filterConfiguration.access.every((x) => x.matches(app))) {
						return false;
					}
				}

				return true;
			})
		},
		search: {
			doesMatchItem: (app, searchTerm) => contains(app?.name, searchTerm)
				|| app.tags
					.some(t => contains(t.name, searchTerm))
		}
	})
	.getHook();

export type AppStoreFilterConfiguration = {
	searchTerm: VersionedValue<string>;
	access: AppAccessFilter[];
	category: CategoryEntry;
};

export type AppStoreProps = {
	model: AppStoreModel;
	hasLoadingAppsError: boolean;
	onRetry(): void;
	permissions: AppStorePermissionsClientModel;
	filterConfiguration: AppStoreFilterConfiguration;
	onFilterConfigurationChange(value: AppStoreFilterConfiguration): void;
	onBusyChange(value: boolean): void;
	onChangeView(view: AppStoreViewKind): void;
	onEditApp(
		target: AppTarget,
		activateWhoHasAccessTab: boolean
	): void;
	onAppUpdated(app: AppModel): void;
	onAppDeleted(id: number): void;
	onClose: () => void;
};

export const AppStore = (props: AppStoreProps): ReactElement => {
	const {
		onBusyChange,
		filterConfiguration,
		onFilterConfigurationChange,
		model,
		hasLoadingAppsError,
		onRetry,
		permissions,
		onChangeView,
		onEditApp,
		onAppUpdated,
		onAppDeleted,
		onClose
	} = props;

	const contentRef: Ref<HTMLDivElement> = useRef<HTMLDivElement>(null);
	const [disableConfirmation, setDisableConfirmation] = useState<{ app: AppModel; tileCount: number; } | null>(null);
	const alertManager: AlertManager = useAlerts();
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
	const sidebarRef: RefObject<SidebarHandle | null> = useRef<SidebarHandle>(null);

	useScrollToTop(
		contentRef,
		[filterConfiguration]
	);

	const {currentItems: appsToRender} = useAppsDataSource(
		model.apps,
		{
			searchTerm: filterConfiguration.searchTerm.value,
			filtering: filterConfiguration
		}
	);

	const disableHandler = async (app: AppModel): Promise<void> => {
		if (!app.installedAppId) {
			throw new Error("Cannot disable the app which is not enabled");
		}
		try {
			onBusyChange(true);
			const tileCount: number = await getTileInstanceCount(app.installedAppId);
			setDisableConfirmation({
				app,
				tileCount
			});
		} catch (e) {
			console.error(e);
			alertManager.showError(localize("_UnknownError"));
		} finally {
			onBusyChange(false);
		}
	};

	const doDisableHandler = async (app: AppModel): Promise<void> => {
		try {
			if (!app.installedAppId) {
				throw new Error("Cannot disable the app which is not enabled");
			}

			onBusyChange(true);
			setDisableConfirmation(null);

			await disableApp(app.installedAppId);

			publish(
				AppStoreEvent.AppDisabled,
				{
					id: app.installedAppId,
					type: app.appType
				}
			);

			if (app.isGoneAfterDisabling) {
				onAppDeleted(app.installedAppId);
			} else {
				app.installedAppId = null;
				onAppUpdated(app);
			}
		} catch (e) {
			console.error(e);
			const key: LocalizationKey = (e instanceof HttpRequestError && e.statusCode === 403)
				? "_YouDoNotHavePermissionForThat"
				: "_GeneralErrorMessage";
			alertManager.showError(localize(key));
		} finally {
			onBusyChange(false);
		}
	};

	const editHandler = (app: AppModel): void => {
		if (app.appType === AppType.Unknown) {
			alertManager.showError(localize("_UnknownInstalledAppMessage"));
			return;
		}

		const installedAppId: number | null = app.installedAppId;
		if (!installedAppId) {
			throw new Error("Cannot edit an app which is not installed");
		}

		onEditApp(
			{
				kind: "installed-app",
				app: {
					...app,
					installedAppId
				}
			},
			false
		);
	};

	const changeCategoryFilterHandler = useCallback(
		(category: CategoryEntry): void => {
			if (isMobile) {
				sidebarRef.current
					?.onClose();
			}

			onFilterConfigurationChange({
				...filterConfiguration,
				category
			});
		},
		[
			filterConfiguration,
			isMobile,
			onFilterConfigurationChange
		]
	);

	const changeAccessFilterHandler = (access: AppAccessFilter[]): void => {
		onFilterConfigurationChange({
			...filterConfiguration,
			access
		});
	};

	const changeSearchFilterHandler = (searchTerm: string, version: Guid): void => {
		onFilterConfigurationChange({
			...filterConfiguration,
			searchTerm: {
				value: searchTerm,
				version
			}
		});
	};
	const createPrimaryAction = (app: AppModel): PrimaryAction | undefined => {
		if (app.installedAppId) {
			if (app.canUserEditApp) {
				return {
					key: "_Edit",
					handler: () => editHandler(app),
					trackingId: "edit",
					kind: "secondary"
				};
			}
		} else {
			return {
				key: "_Enable",
				kind: "primary",
				handler: () => onEditApp(
					{
						kind: "new-app-from-main",
						app: app
					},
					true
				),
				trackingId: "enable"
			};
		}

		return undefined;
	};

	const sidebarExpansionPreferencePersistenceKey: string = useUserScopedKey(AppStoreSidebarKey);

	const categoriesForFilter = useMemo(
		() => categories.filter(x => x.id === null || !hiddenCategoryIds.includes(x.id)),
		[]
	);

	const hasNoResults = useMemo(
		(): boolean => appsToRender.length === 0,
		[appsToRender]
	);

	return (
		<>
			<Dialog.Body goesEdgeToEdge>
				<ContentWithSidebar trackingId="app-store">
					<ContentWithSidebar.Sidebar
						persistentKeyName={sidebarExpansionPreferencePersistenceKey}
						ref={sidebarRef}
					>
						<CategoryFilter
							items={categoriesForFilter}
							value={filterConfiguration.category}
							onChange={changeCategoryFilterHandler}
							hasSectionHeader
						/>
						<AccessFilter
							value={filterConfiguration.access}
							onChange={changeAccessFilterHandler}
						/>
					</ContentWithSidebar.Sidebar>

					<ContentWithSidebar.Content ref={contentRef} hasBackgroundColor>
						<AppStoreLayout fillHeight={hasNoResults}>
							<AppStoreLayout.Header
								actions={
									permissions.canCreateApps
										? (
											<Button
												kind="primary"
												text={localize("_AddNewApp")}
												onClick={() => {
													if (!hasLoadingAppsError) {
														onChangeView("add-new");
													}
												}}
												trackingId="add-new-app"
											/>
										)
										: undefined
								}
								search={
									<SearchInputForm
										value={filterConfiguration.searchTerm}
										onChange={changeSearchFilterHandler}
										placeholder={localize("_SearchApps")}
										trackingId="search-term"
										variant="white"
									/>
								}
							/>

							<AppStoreLayout.CategoryName name={filterConfiguration.category.name} />

							{hasLoadingAppsError && (
								<NoResults
									trackingId="has-error"
									icon="gear"
									title={localize("_NotAbleToRetrieveContacts")}
									message={localize("_WeWereUnableToRetrieveYourContacts")}
									onRetry={onRetry}
									height="fill"
								/>
							)}
							{(hasNoResults && !hasLoadingAppsError)
								? filterConfiguration.searchTerm.value
									? (
										<NoResults
											trackingId="no-search-results"
											icon="magnifying-glass"
											title={localize("_NoSearchResults")}
											message={localize("_PleaseTrySearchingWithAnotherTerm")}
											height="fill"
										/>
									)
									: (
										<NoResults
											trackingId="no-apps"
											icon="grid-2"
											title={localize("_NoAppsYet")}
											message={localize("_ThereAreNoAppsAvailableYet")}
											height="fill"
										/>
									)
								: (
									<AppCardList trackingId="card-list">
										{appsToRender.map((x) => (
											<AppCard
												key={`${x.mainAppId}|${x.installedAppId}`}
												icon={x.imageDisplayInfo}
												color={x.color}
												userLicense={x.userLicense}
												name={x.name}
												category={getCategoryName(x.categoryId)}
												appKind={x.appType}
												access={{
													hasConditionalAccess: x.hasConditionalAccessConfigured,
													hasMaintenance: x.hasMaintenanceConfigured,
													hasOwnerPermissions: x.hasOwnerPermissionsConfigured
												}}
												isFeaturedAsNew={x.isFeaturedAsNew}
												primaryAction={createPrimaryAction(x)}
												secondaryAction={
													x.installedAppId && x.canUserDisableApp
														? {
															handler: () => disableHandler(x),
															trackingId: x.isGoneAfterDisabling ? "delete" : "disable",
															icon: x.isGoneAfterDisabling ? "trash-can" : "ban",
															tooltipKey: x.isGoneAfterDisabling ? "_Delete" : "_Disable"
														}
														: undefined
												}
												tags={x.tags}
												trackingId={generateTrackingId("app-store-card", `|${x.appType}|${x.installedAppId ?? ""}`)}
											/>
										))}
									</AppCardList>
								)
							}
						</AppStoreLayout>
					</ContentWithSidebar.Content>
				</ContentWithSidebar>

				{disableConfirmation && (
					<DisableAppConfirmationDialog
						{...disableConfirmation}
						onConfirmation={() => doDisableHandler(disableConfirmation.app)}
						onCancellation={() => setDisableConfirmation(null)}
					/>
				)}
			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="secondary"
						onClick={onClose}
						text={localize("_Close")}
						responsiveBehavior="hide"
					/>
				</Toolbar>
			</Dialog.Footer>
		</>
	);
};
AppStore.displayName = "AppStore";

const ACCESS_ITEM_ID: string = "access";

type AccessFilterProps = {
	value: readonly AppAccessFilter[];
	onChange(filters: AppAccessFilter[]): void;
};

const AccessFilter = (props: AccessFilterProps): ReactElement => {
	const {
		value,
		onChange
	} = props;

	const toggleAppFilter = (filter: AppAccessFilter, isEnabled: boolean): void => {
		const copy: AppAccessFilter[] = [...value];
		if (isEnabled) {
			copy.push(filter);
		} else {
			const index: number = copy.indexOf(filter);
			if (index !== -1) {
				copy.splice(index, 1);
			}
		}

		onChange(copy);
	};

	const { isExpanded, toggleExpansion } = useToggleExpansion([ACCESS_ITEM_ID]);

	const toggleExpansionHandler = useCallback(
		(): void => toggleExpansion(ACCESS_ITEM_ID),
		[toggleExpansion]
	);

	return (
		<TreeAccordion trackingId="app-access-filter">
			<TreeAccordion.Node>
				<TreeAccordion.Item
					trackingId="app-access-filter___header"
					expansionToggle="visible"
					isExpanded={isExpanded(ACCESS_ITEM_ID)}
					onToggleExpansion={toggleExpansionHandler}
					onClick={toggleExpansionHandler}
				>
					{localize("_Access")}
				</TreeAccordion.Item>
				<TreeAccordion.SubTree
					isCollapsed={!isExpanded(ACCESS_ITEM_ID)}
				>
					{accessFilters.map((x) => (
						<TreeAccordion.Node key={x.id}>
							<TreeAccordion.Item
								trackingId={generateTrackingId("app-access-filter___item", `|${x.id}`)}
								indentionLevel={1}
								stretchContentHeight
							>
								<Checkbox
									text={x.text}
									value={value.indexOf(x) !== -1}
									onChange={(v) => toggleAppFilter(x, v)}
									isCentered
									trackingId={generateTrackingId("app-access-filter__checkbox", `|${x.id}`)}
								/>
							</TreeAccordion.Item>
						</TreeAccordion.Node>
					))}
				</TreeAccordion.SubTree>
			</TreeAccordion.Node>
		</TreeAccordion>
	);
};
AccessFilter.displayName = "AccessFilter";

type DisableAppConfirmationDialogProps = {
	app: AppModel;
	tileCount: number;
	onConfirmation(): void;
	onCancellation(): void;
};

const DisableAppConfirmationDialog = (props: DisableAppConfirmationDialogProps): ReactElement => {
	const {
		app,
		tileCount,
		onConfirmation,
		onCancellation
	} = props;

	let titleKey: LocalizationKey;
	let bodyKey: LocalizationKey;
	let buttonKey: LocalizationKey;
	if (app.isGoneAfterDisabling) {
		titleKey = "_AreYouSureYouWantToDeleteApp";
		bodyKey = "_DeleteThisInstalledApp";
		buttonKey = "_Delete";
	} else {
		titleKey = "_AreYouSureYouWantToDisableApp";
		bodyKey = "_ThisAppIsBeingUsedByAmountTilesDisablingThisAppWillDeleteAllTheseTilesAreYouSureYouWantToDisableThisApp";
		buttonKey = "_Disable";
	}

	return (
		<ConfirmationDialog
			title={
				<FormattedText
					text={localize(titleKey)}
					thingsToReplace={[
						{what: "{{AppName}}", filler: app.name}
					]}
				/>
			}
			confirmText={localize(buttonKey)}
			onConfirmation={onConfirmation}
			onCancellation={onCancellation}
			trackingId="delete-app-confirmation-dialog"
		>
			<FormattedText
				text={localize(bodyKey)}
				thingsToReplace={[
					{what: "{0}", filler: tileCount}
				]}
			/>
		</ConfirmationDialog>
	);
};
DisableAppConfirmationDialog.displayName = "DisableAppConfirmationDialog";

type AppAccessFilter = {
	text: string;
	matches(app: AppModel): boolean;
	id: string;
};

const accessFilters: readonly AppAccessFilter[] = [
	{
		text: localize("_OwnerPermissions"),
		matches: (app) => app.hasOwnerPermissionsConfigured,
		id: "owner-permissions"
	},
	{
		text: localize("_Maintenance"),
		matches: (app) => app.hasMaintenanceConfigured,
		id: "maintenance"
	},
	{
		text: localize("_ConditionalAccess"),
		matches: (app) => app.hasConditionalAccessConfigured,
		id: "conditional-access"
	}
];

export const AppStoreSidebarKey = "appstore:sidebar";
